import {
  Calendar,
  CalendarClock,
  CalendarPlus,
  IndianRupee,
  BarChart3,
  Settings,
  Users,
  ShieldCheck,
  UserSquare2,
  Wrench,
  ClipboardList,
  Receipt,
  Trophy,
  Camera,
  LifeBuoy,
} from 'lucide-react';
import { MODULES, ROLE_NAMES } from '../../constants/modules';

/**
 * Single source of truth for the rearrangeable dashboard module grid.
 *
 * Each entry maps a grid card to a destination route and the RBAC permission
 * that gates it (`module` + `access`, checked via useAuth().hasPermission).
 * `badgeKey` optionally names a live-count prop supplied by the home dashboard.
 */
export const MODULE_REGISTRY = {
  bookings: {
    id: 'bookings',
    title: 'Bookings',
    icon: Calendar,
    route: '/dashboard/book-now',
    module: MODULES.BOOKINGS,
  },
  booking_requests: {
    id: 'booking_requests',
    title: 'Booking Requests',
    icon: CalendarClock,
    route: '/dashboard/booking-requests',
    module: MODULES.BOOKINGS,
    badgeKey: 'pendingRequestCount',
  },
  // 'active_slots' card retired — the booking agenda's "Booked" status chip
  // replaced the Booked Slots screen. reconcile() silently drops the id from
  // previously saved layouts; the backend still accepts it for compatibility.
  individual_booking: {
    id: 'individual_booking',
    title: 'Individual Booking',
    icon: CalendarPlus,
    route: '/dashboard/booking-new',
    module: MODULES.INDIVIDUAL_BOOKING,
    access: 'write',
  },
  payments: {
    id: 'payments',
    title: 'Payments',
    icon: IndianRupee,
    route: '/dashboard/payments',
    module: MODULES.PAYMENTS,
  },
  reports: {
    id: 'reports',
    title: 'Reports',
    icon: BarChart3,
    route: '/dashboard/reports',
    module: MODULES.REPORTS,
  },
  configuration: {
    id: 'configuration',
    title: 'Configuration',
    icon: Settings,
    route: '/dashboard/configure-service',
    module: MODULES.CONFIGURATION,
    access: 'write',
  },
  users: {
    id: 'users',
    title: 'Users',
    icon: Users,
    route: '/dashboard/users',
    module: MODULES.USERS,
  },
  roles: {
    id: 'roles',
    title: 'Roles',
    icon: ShieldCheck,
    route: '/dashboard/roles',
    module: MODULES.ROLES,
  },
  staff: {
    id: 'staff',
    title: 'Staff',
    icon: UserSquare2,
    route: '/dashboard/staff',
    module: MODULES.STAFF,
  },
  equipment: {
    id: 'equipment',
    title: 'Assets',
    icon: Wrench,
    route: '/dashboard/assets',
    module: MODULES.EQUIPMENT,
  },
  operations: {
    id: 'operations',
    title: 'Operations',
    icon: ClipboardList,
    route: '/dashboard/operations',
    module: MODULES.OPERATIONS,
  },
  expenses: {
    id: 'expenses',
    title: 'Expenses',
    icon: Receipt,
    route: '/dashboard/expenses',
    module: MODULES.EXPENSES,
  },
  tournaments: {
    id: 'tournaments',
    title: 'Tournaments',
    icon: Trophy,
    route: '/dashboard/tournaments',
    module: MODULES.TOURNAMENTS,
  },
  selfie_attendance: {
    id: 'selfie_attendance',
    title: 'Selfie Attendance',
    icon: Camera,
    route: '/dashboard/selfie-attendance',
    module: MODULES.SELFIE_ATTENDANCE,
  },
  help_center: {
    id: 'help_center',
    title: 'Help Center',
    icon: LifeBuoy,
    route: '/dashboard/help-center',
    module: MODULES.HELP_CENTER,
  },
};

export const ALL_MODULE_IDS = Object.keys(MODULE_REGISTRY);

/** Canonical fallback order — also the tail order for ids missing from a role default. */
const CANONICAL_ORDER = [
  'bookings',
  'booking_requests',
  'individual_booking',
  'payments',
  'reports',
  'operations',
  'equipment',
  'staff',
  'selfie_attendance',
  'expenses',
  'tournaments',
  'users',
  'roles',
  'configuration',
  'help_center',
];

export const DEFAULT_ORDER_BY_ROLE = {
  [ROLE_NAMES.SUPER_ADMIN]: ['bookings', 'payments', 'reports', 'staff'],
  [ROLE_NAMES.ADMIN]: ['bookings', 'payments', 'reports', 'staff'],
  [ROLE_NAMES.MANAGER]: ['bookings', 'payments', 'staff', 'operations'],
  [ROLE_NAMES.STAFF]: ['selfie_attendance', 'operations', 'equipment'],
};

/** Full default order for a role: role-specific lead, canonical tail. */
export function defaultOrderForRole(roleName) {
  const lead = DEFAULT_ORDER_BY_ROLE[roleName] || [];
  return [...lead, ...CANONICAL_ORDER.filter((id) => !lead.includes(id))];
}

/**
 * Module ids the current user may see, in canonical order.
 * `hasPermission(moduleId, accessType)` comes from useAuth().
 */
export function allowedModuleIds(hasPermission) {
  return CANONICAL_ORDER.filter((id) => {
    const entry = MODULE_REGISTRY[id];
    return hasPermission(entry.module, entry.access || 'read');
  });
}

/**
 * Merge a saved layout with what the user is currently allowed to see.
 *
 * - Drops ids the user can no longer access (role change, module removed)
 * - Appends newly shipped / newly permitted modules at the end, so users with
 *   old saved layouts automatically receive new modules
 * - Never throws on malformed input — falls back to the role default
 */
export function reconcile(saved, roleName, hasPermission) {
  const allowed = allowedModuleIds(hasPermission);

  const savedOrder = Array.isArray(saved?.order) ? saved.order : null;
  if (!savedOrder) {
    const defaults = defaultOrderForRole(roleName).filter((id) => allowed.includes(id));
    return { order: defaults, hidden: [] };
  }

  const order = savedOrder.filter((id) => allowed.includes(id));
  const missing = allowed.filter((id) => !order.includes(id));
  const hidden = (Array.isArray(saved.hidden) ? saved.hidden : []).filter((id) =>
    allowed.includes(id)
  );

  return { order: [...order, ...missing], hidden };
}
